import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { useTranslation } from 'react-i18next'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Status } from '#app/utils/status.ts'
import { EmailSchema } from '#app/utils/user-validation.ts'
import OnboardingShapes from './onboarding-shapes.tsx'

const InvalidCodeSchema = z.object({
  email: EmailSchema,
})

interface InvalidCodeFormProps {
  fetcher: any
  instructionsText: string
  emailSuccessfullySentText: string
}

export default function InvalidCodeForm({
  fetcher,
  instructionsText,
  emailSuccessfullySentText,
}: InvalidCodeFormProps) {
  const { t } = useTranslation()

  const [form, fields] = useForm({
    id: 'invalid-code-form',
    constraint: getZodConstraint(InvalidCodeSchema),
    lastResult: fetcher.data?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: InvalidCodeSchema })
    },
    shouldRevalidate: 'onInput',
  })

  const emailWasSuccessfullySent = fetcher?.data?.status === Status.Success

  return (
    <main
      id="main-content"
      className="container flex flex-col items-center justify-center pb-32"
    >
      <div className="relative w-[460px]">
        <OnboardingShapes />
        <div className="relative">
          <div className="mt-52">
            <h1 className="font-poppins text-4 font-medium">
              {t('invalidCode.header')}
            </h1>
            <h2 className="text-2 mt-3">{instructionsText}</h2>

            <fetcher.Form method="POST" {...getFormProps(form)}>
              <HoneypotInputs />
              <div className="mt-6">
                <Field
                  labelProps={{
                    htmlFor: fields.email.id,
                    children: t('invalidCode.fieldLabel'),
                  }}
                  inputProps={{
                    autoFocus: true,
                    placeholder: t('signup.email.fieldPlaceholder'),
                    ...getInputProps(fields.email, { type: 'email' }),
                  }}
                  errors={fields.email.errors}
                />
              </div>

              {form.errors && form.errors.length > 0 && (
                <div className="mb-5">
                  <ErrorList errors={form.errors} id={form.errorId} />
                </div>
              )}
              <div>
                {emailWasSuccessfullySent && (
                  <p className="text-2 mb-4">{emailSuccessfullySentText}</p>
                )}
                {!emailWasSuccessfullySent && (
                  <StatusButton
                    status={
                      fetcher.state === 'submitting'
                        ? 'pending'
                        : (fetcher.data?.status ?? 'idle')
                    }
                    type="submit"
                    disabled={fetcher.state !== 'idle'}
                  >
                    {t('invalidCode.buttonText')}
                  </StatusButton>
                )}
              </div>
            </fetcher.Form>
          </div>
        </div>
      </div>
    </main>
  )
}
